import { useState } from 'react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Stepper } from '../ui/Stepper';
import { DeleteButton } from '../ui/DeleteButton';
import { NumberInput } from '../ui/NumberInput';
import { useAdmin } from '../AdminContext';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { CoursePrepaidBlock } from './CoursePrepaidBlock';
import { KuurStopForm } from './KuurStopForm';
import { today } from '../../core/format/dates';
import { isKuurExpired } from '../../core/rules/kuur';
import type { Course } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';

/** Een kuur telt minstens twee sessies; anders is het gewoon een behandeling. */
const MIN_COURSE_SESSIONS = 2;
/** Als geen enkele behandeling een kuur heeft, begint het veld hierop. */
const DEFAULT_COURSE_SESSIONS = 6;

/** Kuur starten, bijhouden of beëindigen. De klant ziet de voortgang in Mijn omgeving. */
export function CourseCard({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    catalog,
    index,
    site
  } = useAdmin();
  const policy = site.policy;
  const course = ctl.file.account.course;
  const firstKuur = catalog.treatments.find(t => t.kuur) ?? catalog.treatments[0];
  const [slug, setSlug] = useState(firstKuur?.slug ?? '');
  const [total, setTotal] = useState(Math.min(firstKuur?.kuur?.sessions ?? DEFAULT_COURSE_SESSIONS, policy.courseMaxSessions));
  const [stopping, setStopping] = useState(false);
  const pick = (next: string) => {
    setSlug(next);
    const sessions = index.treatmentBySlug(next)?.kuur?.sessions;
    if (sessions) setTotal(sessions);
  };
  if (!course) {
    return <Panel title="Kuur" description="Start een kuur om de sessies bij te houden. De klant ziet de voortgang in Mijn omgeving." data-id="element-1122" data-mp="11v2dwfwcxlv">
        <div className="space-y-4" data-id="element-1123" data-mp="22vkpmzrjbhvi">
          <Field label="Behandeling" data-id="element-1124" data-mp="22wm8hqesl8ns">
            <select value={slug} onChange={e => pick(e.target.value)} className={inputCls} data-id="element-1125" data-mp="22xp1c3vodl6c">
              {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-1126" data-mp="3b6cr109au5b9">
                  {t.name}
                  {t.kuur ? ` · kuur van ${t.kuur.sessions}` : ''}
                </option>)}
            </select>
          </Field>
          <Field label="Aantal sessies" data-id="element-1127" data-mp="3k7kl164s8jzj">
            <NumberInput value={total} onChange={v => v !== undefined && setTotal(v)} min={MIN_COURSE_SESSIONS} max={policy.courseMaxSessions} className="max-w-[8rem]" data-id="element-1128" data-mp="3f76drqb09e2k" />
          </Field>
          <button type="button" onClick={() => ctl.saveCourse({
          treatmentSlug: slug,
          total,
          done: 0
        })} disabled={!slug || ctl.isBusy('course')} aria-busy={ctl.isBusy('course')} className={btnPrimary} data-id="element-1129" data-mp="3p300u3mcopyl">
            {ctl.isBusy('course') ? 'Starten…' : 'Kuur starten'}
          </button>
        </div>
      </Panel>;
  }
  const t = index.treatmentBySlug(course.treatmentSlug);
  const finished = course.done >= course.total;
  const set = (patch: Partial<Course>) => ctl.saveCourse({
    ...course,
    ...patch
  });
  // Vooruit betaald en nog geldig: stoppen gaat met een creditnota (ook bij € 0 terug, met reden).
  // Per keer betaald of verlopen: gewoon beëindigen, er gaat geen geld terug.
  const canRefund = Boolean(course.prepaid && !isKuurExpired(course, today()));
  return <Panel title="Kuur" description={t?.name ?? course.treatmentSlug} data-id="element-1130" data-mp="qbbstbstlpop">
      <p className="font-display text-[34px] font-light leading-none text-ink" aria-hidden="true" data-id="element-1131" data-mp="cl8bmz8ssm64">
        {course.done}
        <span className="text-[20px] text-muted" data-id="element-1132" data-mp="hd04xhjc95zy"> / {course.total}</span>
      </p>
      <div className="mt-3 flex gap-1" role="meter" aria-valuemin={0} aria-valuemax={course.total} aria-valuenow={course.done} aria-label={`${course.done} van ${course.total} sessies gehad`} data-id="element-1133" data-mp="2o3qn2qouwqw">
        {Array.from({
        length: course.total
      }, (_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i < course.done ? 'bg-clay' : 'bg-sand'}`} data-id="element-1134" data-mp="3tskbfxvdgtkh" />)}
      </div>
      {finished && <p className="mt-3 text-[13px] text-ink" data-id="element-1135" data-mp="3ms9omqsl8pxe">Afgerond. Een goed moment om onderhoud of een thuisroutine voor te stellen.</p>}

      <CoursePrepaidBlock course={course} treatment={t} validMonths={policy.kuurValidMonths} busy={ctl.isBusy('course')} onSave={next => ctl.saveCourse(next)} data-id="element-1136" data-mp="3d03x1i690aiq" />

      <div className="mt-5 space-y-3 border-t border-line pt-4 text-[14px]" data-id="element-1137" data-mp="3407elyagc7w4">
        <div className="flex items-center justify-between gap-3" data-id="element-1138" data-mp="2z148eaiqjvmk">
          <span className="text-muted" data-id="element-1139" data-mp="2yzjgksnt2qse">Sessies gehad</span>
          <Stepper label="Sessies gehad" value={course.done} min={0} max={course.total} onChange={done => set({
          done
        })} data-id="element-1140" data-mp="2u0yfys4n653f" />
        </div>
        <div className="flex items-center justify-between gap-3" data-id="element-1141" data-mp="2p4hs7z1ctsqv">
          <span className="text-muted" data-id="element-1142" data-mp="s9but77fl4ev">Totaal</span>
          {course.prepaid ?
        // Vooruit betaald: het aantal hoort bij wat ze kocht (en bij het terugbetalen). Vast.
        <span className="text-ink" title="Vooruit betaald: het aantal sessies ligt vast" data-id="element-1143" data-mp="vt3x62kv3xwn">
              {course.total} <span className="text-[12px] text-muted" data-id="element-1144" data-mp="zsuihqrmdlts">· vast, vooruit betaald</span>
            </span> : <Stepper label="Totaal aantal sessies" value={course.total} min={Math.max(1, course.done)} max={Math.max(policy.courseMaxSessions, course.total)} onChange={next => set({
          total: next
        })} data-id="element-1145" data-mp="14l5v9u7gl23r" />}
        </div>
      </div>
      <div className="mt-4 border-t border-line pt-3" data-id="element-1146" data-mp="gx4exz41ghds">
        {canRefund && course.prepaid ? stopping ? <KuurStopForm ctl={ctl} course={{
        ...course,
        prepaid: course.prepaid
      }} treatment={t} onClose={() => setStopping(false)} data-id="element-1147" data-mp="qt7t63c606h3" /> : <button type="button" onClick={() => setStopping(true)} disabled={ctl.isBusy('course')} className={btnSecondary} data-id="element-1148" data-mp="1x8i8vevn5zkx">
              Kuur stoppen en terugbetalen…
            </button> : <DeleteButton label="Kuur beëindigen" warning="Voortgang verdwijnt uit Mijn omgeving" onConfirm={() => ctl.saveCourse(null)} data-id="element-1149" data-mp="1ciea2vngi6fg" />}
      </div>
    </Panel>;
}